import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, CheckCircle2, ArrowRight, Share2, MoreVertical, ExternalLink } from 'lucide-react';

export default function PWAInstallPrompt({ lang = 'mr', currentTenant = null }) {
  const isMr = lang === 'mr';
  const dairyName = isMr 
    ? (currentTenant?.brand_name_mr || currentTenant?.brandMarathi || currentTenant?.name || 'MilkPro Mitra')
    : (currentTenant?.brand_name_en || currentTenant?.brandEnglish || currentTenant?.name || 'MilkPro Mitra');

  const [deferredPrompt, setDeferredPrompt] = useState(window.deferredPwaPrompt || null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showManualGuide, setShowManualGuide] = useState(false);

  useEffect(() => {
    // 1. If already installed as standalone PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    const isInstalledFlag = localStorage.getItem('milkpro_pwa_installed') === 'true';

    if (isStandalone || isInstalledFlag) {
      return;
    }

    // 2. Check device and browser type
    const userAgent = window.navigator.userAgent || '';
    const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || 
                          (typeof window !== 'undefined' && window.innerWidth <= 768);
    const isApple = /iPhone|iPad|iPod/i.test(userAgent);
    const inApp = /FBAN|FBAV|Instagram|WhatsApp|Line|FB_IAB/i.test(userAgent);

    setIsIOS(isApple);
    setIsInAppBrowser(inApp);

    // 3. Early sniffer check
    if (window.deferredPwaPrompt) {
      setDeferredPrompt(window.deferredPwaPrompt);
      if (isMobileDevice) setIsVisible(true);
    }

    window.onPwaPromptReady = (e) => {
      setDeferredPrompt(e);
      if (isMobileDevice) setIsVisible(true);
    };

    // 4. Fallback listener
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredPwaPrompt = e;
      if (isMobileDevice) setIsVisible(true);
    };

    const handleAppInstalled = () => {
      localStorage.setItem('milkpro_pwa_installed', 'true');
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    let timer;
    if (isMobileDevice) {
      timer = setTimeout(() => {
        setIsVisible(true);
      }, 600);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.onPwaPromptReady = null;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Main Install Action
  const handleInstallClick = async () => {
    if (isIOS) {
      setShowManualGuide(true);
      return;
    }

    const activePrompt = deferredPrompt || window.deferredPwaPrompt;

    if (activePrompt) {
      try {
        await activePrompt.prompt();
        const choice = await activePrompt.userChoice;
        if (choice && choice.outcome === 'accepted') {
          localStorage.setItem('milkpro_pwa_installed', 'true');
          setInstalled(true);
        }
        setDeferredPrompt(null);
        window.deferredPwaPrompt = null;
      } catch (err) {
        console.error('Install error:', err);
        setShowManualGuide(true);
      }
    } else {
      // Browser didn't provide native programmatic prompt
      setShowManualGuide(true);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleCloseAfterInstall = () => {
    localStorage.setItem('milkpro_pwa_installed', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      zIndex: 999999,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      padding: '0 0 16px 0',
      animation: 'fadeIn 0.25s ease'
    }}>
      <div style={{
        background: '#ffffff',
        width: '100%',
        maxWidth: 440,
        margin: '0 12px',
        borderRadius: 22,
        padding: '20px',
        boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.45)',
        border: '1.5px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        position: 'relative'
      }}>
        
        {/* Dismiss X button */}
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: '50%',
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            cursor: 'pointer'
          }}
          title={isMr ? 'बंद करा' : 'Close'}
        >
          <X size={16} />
        </button>

        {/* State 1: App Installed Successfully */}
        {installed ? (
          <div style={{ textAlign: 'center', padding: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: '#dcfce7',
              border: '3px solid #86efac',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle2 size={36} color="#16a34a" />
            </div>
            <div>
              <div style={{ fontSize: '1.18rem', fontWeight: 900, color: '#0f172a' }}>
                {isMr ? '🎉 ॲप यशस्वीरीत्या इन्स्टॉल झाले!' : '🎉 App Installed Successfully!'}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 800, marginTop: 4 }}>
                {dairyName} ॲप आता थेट तुमच्या मोबाईल स्क्रीनवर उपलब्ध आहे.
              </div>
            </div>
            <button
              type="button"
              onClick={handleCloseAfterInstall}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: '#fff',
                fontWeight: 900,
                fontSize: '0.92rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              <span>{isMr ? 'ॲप उघडा' : 'Open App'}</span>
              <ArrowRight size={18} />
            </button>
          </div>
        ) : (
          <>
            {/* Header: Logo & Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 32 }}>
              <img
                src="/milkpro_logo.jpg"
                alt="MilkPro Mitra"
                style={{ width: 50, height: 50, borderRadius: 14, border: '1.5px solid #cbd5e1', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
              />
              <div>
                <div style={{ fontWeight: 900, fontSize: '1.05rem', color: '#0f172a', lineHeight: 1.2 }}>
                  {isMr ? 'मोबाईल ॲप इन्स्टॉल करा' : 'Install Mobile App'}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#0284c7', fontWeight: 800, marginTop: 2 }}>
                  {dairyName}
                </div>
              </div>
            </div>

            {/* Special Notice if opened inside WhatsApp Webview */}
            {isInAppBrowser ? (
              <div style={{
                background: '#fef3c7',
                borderRadius: 12,
                padding: '12px 14px',
                border: '1.5px solid #fde047',
                fontSize: '0.82rem',
                color: '#854d0e',
                lineHeight: 1.5
              }}>
                <strong>⚠️ तुम्ही व्हॉट्सॲपच्या इन-ॲप ब्राऊझरमध्ये आहात:</strong>
                <div style={{ marginTop: 4 }}>
                  मोबाईलवर ॲप इन्स्टॉल करण्यासाठी वरील उजव्या कोपऱ्यातील <strong>३ ठिपके ( <MoreVertical size={13} style={{ verticalAlign: 'middle' }} /> )</strong> दाबून <strong>'Open in Chrome' (Chrome मध्ये उघडा)</strong> निवडा.
                </div>
              </div>
            ) : (
              /* Benefit bullets */
              <div style={{
                background: '#f8fafc',
                borderRadius: 12,
                padding: '10px 14px',
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                fontSize: '0.8rem',
                color: '#334155'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                  <Smartphone size={15} color="#0284c7" />
                  <span>{isMr ? '१-क्लिकने मोबाईल स्क्रीनवरून थेट ॲप सुरू होते.' : '1-Click launch directly from Home Screen.'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                  <Download size={15} color="#16a34a" />
                  <span>{isMr ? 'ब्राऊझरमध्ये वेबसाईट लिंक शोधण्याची गरज नाही.' : 'No need to search or type website link.'}</span>
                </div>
              </div>
            )}

            {/* Direct Install Button */}
            <button
              type="button"
              onClick={handleInstallClick}
              style={{
                width: '100%',
                padding: '13px 18px',
                borderRadius: 14,
                border: 'none',
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                fontSize: '0.96rem',
                fontWeight: 900,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: '0 4px 14px rgba(2, 132, 199, 0.4)'
              }}
            >
              <Download size={19} />
              <span>{isMr ? '📲 ॲप इन्स्टॉल करा (Install App)' : '📲 Install App on Phone'}</span>
            </button>

            {/* Direct 2-step helper guide */}
            {showManualGuide && !isInAppBrowser && (
              <div style={{
                background: '#eff6ff',
                borderRadius: 12,
                padding: '12px 14px',
                border: '1.5px solid #bfdbfe',
                fontSize: '0.82rem',
                color: '#1e40af',
                lineHeight: 1.5
              }}>
                <strong>{isMr ? '💡 क्रोम ब्राऊझरमधून थेट इन्स्टॉल करण्याची सोपी पद्धत:' : '💡 Simple steps in Chrome:'}</strong>
                {isIOS ? (
                  <ol style={{ margin: '6px 0 0 16px', padding: 0 }}>
                    <li style={{ marginBottom: 4 }}>सफारी ब्राऊझरमधील <strong>Share ( <Share2 size={13} style={{ verticalAlign: 'middle' }} /> )</strong> आयकॉन दाबा.</li>
                    <li>खाली स्क्रोल करून <strong>'Add to Home Screen' (स्क्रीनवर जोडा)</strong> निवडा.</li>
                  </ol>
                ) : (
                  <ol style={{ margin: '6px 0 0 16px', padding: 0 }}>
                    <li style={{ marginBottom: 4 }}>तुमच्या मोबाईलच्या क्रोम ब्राऊझरमध्ये वरील उजव्या कोपऱ्यातील <strong>३ ठिपके ( <MoreVertical size={13} style={{ verticalAlign: 'middle' }} /> )</strong> दाबा.</li>
                    <li>त्यातील <strong>'Install app' (ॲप इन्स्टॉल करा)</strong> किंवा <strong>'Add to Home screen' (स्क्रीनवर जोडा)</strong> वर क्लिक करा.</li>
                  </ol>
                )}
              </div>
            )}

            {/* Continue in browser */}
            <button
              type="button"
              onClick={handleDismiss}
              style={{
                width: '100%',
                padding: '6px',
                background: 'transparent',
                border: 'none',
                color: '#64748b',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              {isMr ? 'सध्या ब्राऊझरमध्येच चालू ठेवा' : 'Continue in browser'}
            </button>
          </>
        )}

      </div>
    </div>
  );
}
